import { styled } from "@linaria/react"
import { useNavigate } from "@tanstack/react-router"
import { observer } from "mobx-react-lite"
import { useMemo, useRef } from "react"

import { Gap, Horizontal } from "@/components/Flex.ts"
import { createLLMLogPagesModel } from "@/components/LLMLogChat/LLMLogPagesModel.ts"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { useModel } from "@/hooks/useModel.ts"
import { useQueries } from "@/hooks/useRootContext.ts"
import { CloseLight, Icon16 } from "@/icons/Icons.ts"
import { formatDateTime, formatDuration } from "@/lib/date.ts"
import { creditsToDollars, formatDollars } from "@/lib/displayUtils.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { ScrollableModel } from "@/components/Scrollable/ScrollableModel.ts"
import type { LLMLogPage, LlmTokenStats } from "@/lib/client/generated/TypeDefinitions.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"

export const LLMLogPagesList = observer((props: {
  model: TaskViewModel
  scrollableModel: ScrollableModel
  onPageSelect: (pageId: string) => void
  selectedPageId: string | null
  messageId: string | null
}) => {
  const task = props.model.task.get()
  const taskId = task?.id
  const queries = useQueries()
  const navigate = useNavigate()

  const loadMorePressed = useRef(false)

  const pagesModel = useModel(() => {
    return taskId ? createLLMLogPagesModel(taskId, queries, props.messageId) : null
  }, [taskId, props.messageId])

  const clearFilter = () => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, filterMessageId: undefined })
    })
  }

  if (!pagesModel) {
    return null
  }

  return (
    <Container>
      {props.messageId && (
        <>
          <FilterBadge>
            <FilterBadgeText>
              Filtered by message: {props.messageId}
            </FilterBadgeText>
            <FilterBadgeClear onClick={clearFilter} title="Clear filter">
              <CloseLight className={Icon16} />
            </FilterBadgeClear>
          </FilterBadge>
          <Gap height="8px" />
        </>
      )}

      {!pagesModel.isReady.get() ? (
        <LoadMoreContainer>
          <Spinner text={ellipsize("Loading pages")} />
        </LoadMoreContainer>
      ) : pagesModel.hasPrev.get() && (
        <LoadMoreContainer>
          {pagesModel.isLoadingPrev.get() ? (
            <Spinner text={ellipsize("Loading")} />
          ) : (
            <LoadMoreLink 
              href="#" 
              onClick={(e) => {
                e.preventDefault()
                loadMorePressed.current = true
                pagesModel.loadPrev()
              }}
            >
              Load previous pages...
            </LoadMoreLink>
          )}
        </LoadMoreContainer>
      )}

      <Gap height="8px" />

      {pagesModel.pages.get().map(page => (
        <PageItem
          key={page.id}
          page={page}
          isSelected={props.selectedPageId === page.id}
          onClick={() => props.onPageSelect(page.id)}
        />
      ))}

      {pagesModel.hasNext.get() && (
        <LoadMoreContainer>
          {pagesModel.isLoadingNext.get() ? (
            <Spinner text={ellipsize("Loading")} />
          ) : (
            <LoadMoreLink 
              href="#" 
              onClick={(e) => {
                e.preventDefault()
                loadMorePressed.current = true
                pagesModel.loadNext()
              }}
            >
              Load more pages...
            </LoadMoreLink>
          )}
        </LoadMoreContainer>
      )}
    </Container>
  )
})

const formatTokenCount = (tokens: number) => {
  if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(1)}k`
  }
  return `${tokens}`
}

const formatTokenStats = (stats: LlmTokenStats | null) => {
  if (!stats) return null
  const input = formatTokenCount(stats.inputTokens)
  const output = formatTokenCount(stats.outputTokens)
  return `${input} in / ${output} out`
}

type CacheCostImpact = {
  text: string
  type: "saved" | "extra"
}

const formatCacheCostImpact = (stats: LlmTokenStats | null): CacheCostImpact | null => {
  if (!stats) return null
  
  const cacheRead = stats.cacheReadInputTokens ?? 0
  const cacheWrite = stats.cacheWriteInputTokens ?? 0
  
  if (cacheRead === 0 && cacheWrite === 0) return null
  
  const totalInputTokens = stats.inputTokens
  if (totalInputTokens === 0) return null
  
  // Cost calculation based on Anthropic pricing:
  // - Cache write: 1.25x base input price
  // - Cache read: 0.1x base input price
  // - Regular input: 1x base input price
  
  const regularTokens = totalInputTokens - cacheRead - cacheWrite
  const costWithoutCache = totalInputTokens // All at 1x
  const costWithCache = regularTokens + (cacheRead * 0.1) + (cacheWrite * 1.25)
  
  const savings = costWithoutCache - costWithCache
  const savingsPercent = (savings / costWithoutCache) * 100
  
  if (savingsPercent > 0) {
    return { text: `${savingsPercent.toFixed(0)}% saved`, type: "saved" }
  } else if (savingsPercent < 0) {
    return { text: `${Math.abs(savingsPercent).toFixed(0)}% extra`, type: "extra" }
  }
  return null
}

const PageItem = observer((props: {
  page: LLMLogPage
  isSelected: boolean
  onClick: () => void
}) => {
  const time = formatDateTime(props.page.createdAt)
  
  const cacheCostImpact = useMemo(() => formatCacheCostImpact(props.page.tokenStats), [props.page.tokenStats])
  
  const stats = useMemo(() => {
    const parts: string[] = []
    
    if (props.page.durationMs) {
      parts.push(formatDuration(props.page.durationMs))
    }
    
    if (props.page.requestCount > 0) {
      parts.push(`${props.page.requestCount} ${props.page.requestCount === 1 ? "request" : "requests"}`)
    }
    
    const tokenStats = formatTokenStats(props.page.tokenStats)
    if (tokenStats) parts.push(tokenStats)
    
    if (props.page.creditsSpent) {
      parts.push(`$${formatDollars(creditsToDollars(props.page.creditsSpent))}`)
    }
    
    return parts.join(" · ")
  }, [props.page])

  return (
    <PageItemContainer
      isSelected={props.isSelected}
      onClick={props.onClick}
    >
      <PageItemContent>
        <PageItemLabel>{props.page.label}</PageItemLabel>
        <PageItemMeta>
          <PageItemTime>{time}</PageItemTime>
          {stats && <PageItemStats>{stats}</PageItemStats>}
          {cacheCostImpact && (
            <PageItemCacheImpact type={cacheCostImpact.type}>
              {cacheCostImpact.text}
            </PageItemCacheImpact>
          )}
        </PageItemMeta>
      </PageItemContent>
    </PageItemContainer>
  )
})

const Container = styled.div`
  width: 100%;
`

const LoadMoreContainer = styled.div`
  padding: 8px 0;
  text-align: center;
`

const LoadMoreLink = styled.a`
  ${textStyles.smallFontSize};
  color: var(--main-color);
  
  &:hover {
    color: var(--main-color-hover);
  }
`

const PageItemContainer = styled.div<{ isSelected: boolean }>`
  padding: 12px;
  margin-bottom: 8px;
  border-radius: var(--border-radius-6);
  border: 1px solid ${props => props.isSelected ? "var(--main-color)" : "var(--text-color-10)"};
  background-color: ${props => props.isSelected ? "var(--main-color-5)" : "transparent"};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.isSelected ? "var(--main-color-10)" : "var(--text-color-5)"};
    border-color: ${props => props.isSelected ? "var(--main-color)" : "var(--text-color-20)"};
  }
`

const PageItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const PageItemLabel = styled.div`
  ${textStyles.baseFontSize};
  font-weight: 500;
  color: var(--text-color);
`

const PageItemMeta = styled(Horizontal)`
  gap: 16px;
  flex-wrap: wrap;
`

const PageItemTime = styled.div`
  ${textStyles.smallFontSize};
  color: var(--text-color-40);
`

const PageItemStats = styled.div`
  ${textStyles.smallFontSize};
  color: var(--text-color-40);
`

const PageItemCacheImpact = styled.div<{ type: "saved" | "extra" }>`
  ${textStyles.smallFontSize};
  color: ${props => props.type === "saved" ? "var(--success-color)" : "var(--warning-color)"};
`

const FilterBadge = styled(Horizontal)`
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--main-color-10);
  border: 1px solid var(--main-color-20);
  border-radius: var(--border-radius-6);
`

const FilterBadgeText = styled.span`
  ${textStyles.smallFontSize};
  color: var(--text-color);
  flex: 1;
`

const FilterBadgeClear = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  background: none;
  border: none;
  border-radius: var(--border-radius-4);
  cursor: pointer;
  color: var(--text-color-40);
  
  &:hover {
    background: var(--text-color-10);
    color: var(--text-color);
  }
`

